/**
 * Offres d'achat d'une SKILLCARD (migration 0304) : la carte seule, le pack du
 * chapitre, tout le programme de l'année, la durée du deck, puis l'alternative
 * « Tout JeRaisonne » (pass / abonnement). Partagé par la page d'achat
 * `/card/$cardId/acheter` et le bilan de fin des questions gratuites du player.
 *
 * Web (Stripe), Android (Google Play) et iOS (App Store), migration 0351.
 * Dans l'app native : ni « faire payer par un proche », ni carte cadeau, ni
 * pass Stripe (web seulement).
 */
import { useState, type ReactNode } from 'react';
import { Crown, Layers, Library, Sparkles } from 'lucide-react';
import { Button, cn } from '@jeraisonne/ui';
import { SCHOOL_LEVELS, SUBJECTS } from '@jeraisonne/db';
import {
  cardPalette,
  formatEuros,
  type SkillcardOfferKind,
  type SkillcardQuote,
  offerContentLabel,
} from '@jeraisonne/db/cards';
import {
  skillcardCheckoutErrorMessage,
  startSkillcardCheckout,
  useSkillcardOffers,
} from '@/lib/skillcards/purchase';
import { usePublicCard } from '@/lib/cards/api';
import { supabase } from '@/lib/supabase';
import { PASS_PRICES } from '@/components/exercise-paywall-card';
import {
  DURATION_LABELS,
  passCheckoutLabel,
  passSublabel,
  usePassChoice,
} from '@/components/pass-duration-grid';
import { availablePassDurations } from '@jeraisonne/db/offers';
import {
  createPayRequest,
  payRequestErrorMessage,
  payRequestShareText,
  payRequestUrl,
} from '@/lib/skillcards/pay-request';
import { shareContent } from '@/lib/native-share';
import { isNativeApp } from '@/lib/platform';
import { storePaymentNote } from '@/lib/skillcards/iap';
import { DeckNote } from './deck-note';
import { GiftOffersSection } from './gift-offers-section';
import { SkillcardOfferRow } from './skillcard-offer-row';
import { markCheckoutStarted, passCheckoutMark } from '@/lib/analytics/track';
import { SkillcardPromoBanner } from '@/features/skillcards/skillcard-promo-banner';

const LEVEL_LABELS: Record<string, string> = Object.fromEntries(
  SCHOOL_LEVELS.map((l) => [l.value, l.label]),
);
const SUBJECT_LABELS: Record<string, string> = Object.fromEntries(
  SUBJECTS.map((s) => [s.value, s.label]),
);

function levelSubject(q: SkillcardQuote): string {
  return [
    q.level && (LEVEL_LABELS[q.level] ?? q.level),
    q.subject && (SUBJECT_LABELS[q.subject] ?? q.subject),
  ]
    .filter(Boolean)
    .join(' · ');
}

export function SkillcardOffersPanel({
  cardId,
  userId,
  signedIn,
  returnPath,
  onBeforeCheckout,
  giftOpen = false,
}: {
  cardId: string;
  userId: string | null;
  signedIn: boolean;
  /** Chemin interne où revenir après paiement (exercice, carte) ; null = la carte. */
  returnPath: string | null;
  /** Juste avant de partir vers Stripe (ex. mémoriser la sortie de l'exercice). */
  onBeforeCheckout?: () => void;
  /** Ouvre d'emblée « Offrir à quelqu'un » (lien « Offrir » de la page d'une carte). */
  giftOpen?: boolean;
}) {
  const offers = useSkillcardOffers(cardId, userId);
  const publicCard = usePublicCard(cardId);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [asking, setAsking] = useState<string | null>(null);
  const [asked, setAsked] = useState<{ url: string; copied: boolean } | null>(null);

  // « Faire payer par un proche » : crée (ou réutilise) le lien et le partage.
  async function askPay(kind: SkillcardOfferKind, targetId: string) {
    setError(null);
    setAsked(null);
    setAsking(`${kind}:${targetId}`);
    try {
      const req = await createPayRequest({ kind, targetId, cardId });
      const url = payRequestUrl(req.token);
      const result = await shareContent({
        title: 'Tu peux m’offrir des SKILLCARDS ?',
        text: payRequestShareText(req.label, req.price_cents),
        url,
      });
      setAsked({ url, copied: result === 'copied' });
    } catch (e) {
      setError(payRequestErrorMessage((e as Error).message));
    } finally {
      setAsking(null);
    }
  }
  const storeNote = storePaymentNote();
  const askProps = (kind: SkillcardOfferKind, targetId: string) =>
    signedIn && !isNativeApp()
      ? {
          onAskPay: () => void askPay(kind, targetId),
          askBusy: asking === `${kind}:${targetId}`,
        }
      : {};

  async function buy(kind: SkillcardOfferKind, targetId: string) {
    setError(null);
    setBusy(`${kind}:${targetId}`);
    onBeforeCheckout?.();
    try {
      const url = await startSkillcardCheckout({ kind, targetId, cardId, returnPath });
      window.location.href = url;
    } catch (e) {
      setError(skillcardCheckoutErrorMessage((e as Error).message));
      setBusy(null);
    }
  }

  const data = offers.data;
  if (offers.isLoading) return <div className="bg-ink-100 h-40 animate-pulse rounded-2xl" />;
  if (!data) return null;
  // Les offres prennent le thème de la carte (teinte du niveau).
  const palette = publicCard.data
    ? cardPalette(publicCard.data.card.levels, publicCard.data.card.color_theme)
    : null;
  const theme = palette ? { accent: palette.accent, background: palette.bg } : {};

  return (
    <div className="space-y-4">
      <SkillcardPromoBanner />
      {error && (
        <p role="alert" className="text-error-600 text-sm">
          {error}
        </p>
      )}
      {asked && (
        <div
          role="status"
          className="bg-success-50 text-success-800 space-y-1 rounded-2xl p-4 text-sm"
        >
          <p className="font-semibold">
            {asked.copied ? 'Lien copié ! Envoie-le à la personne de ton choix.' : 'Lien envoyé !'}
          </p>
          <p>Tu seras prévenu(e) dès que c’est payé : les cartes s’ajouteront à ton deck.</p>
          <p className="break-all text-xs opacity-80">{asked.url}</p>
        </div>
      )}

      <Section icon={<Sparkles className="h-4 w-4" />} title="La carte seule">
        <SkillcardOfferRow
          quote={data.card}
          title={data.card.label}
          subtitle={offerContentLabel(data.card)}
          highlight="Le moins cher"
          {...theme}
          busy={busy === `card:${data.card.target_id}`}
          disabled={!signedIn || !!busy}
          onBuy={() => void buy('card', data.card.target_id)}
          {...askProps('card', data.card.target_id)}
        />
      </Section>

      {data.packs.length > 0 && (
        <Section icon={<Layers className="h-4 w-4" />} title="Le pack du chapitre">
          {data.packs.map((p) => (
            <SkillcardOfferRow
              key={p.target_id}
              quote={p}
              title={p.label}
              subtitle={`${offerContentLabel(p)} · ${levelSubject(p)}`}
              {...theme}
              busy={busy === `pack:${p.target_id}`}
              disabled={!signedIn || !!busy}
              onBuy={() => void buy('pack', p.target_id)}
              {...askProps('pack', p.target_id)}
            />
          ))}
        </Section>
      )}

      {data.programmes.length > 0 && (
        <Section icon={<Library className="h-4 w-4" />} title="Tout le programme de l’année">
          {data.programmes.map((p) => (
            <SkillcardOfferRow
              key={p.target_id}
              quote={p}
              title={levelSubject(p) || p.label}
              subtitle={`${offerContentLabel(p)} — toute l’année`}
              highlight="Le plus avantageux"
              {...theme}
              busy={busy === `programme:${p.target_id}`}
              disabled={!signedIn || !!busy}
              onBuy={() => void buy('programme', p.target_id)}
              {...askProps('programme', p.target_id)}
            />
          ))}
        </Section>
      )}

      <DeckNote userId={userId} validityMonths={data.settings.validity_months} />

      <GiftOffersSection cardId={cardId} signedIn={signedIn} defaultOpen={giftOpen} />

      {!isNativeApp() && (
        <SubscriptionAlternative
          signedIn={signedIn}
          returnTo={returnPath ?? `/card/${cardId}`}
          onBeforeCheckout={onBeforeCheckout}
        />
      )}

      <p className="text-ink-500 text-xs leading-relaxed">
        {storeNote ? (
          storeNote
        ) : (
          <>
            Paiement unique sécurisé par Stripe, sans abonnement. Prix unitaire :{' '}
            {formatEuros(data.settings.unit_price_cents)} la carte.
          </>
        )}
      </p>
    </div>
  );
}

/**
 * Alternative à l'achat de cartes : le pass / l'abonnement JeRaisonne, qui
 * inclut TOUTES les SKILLCARDS (`skillcard_premium_reason`). Même tarifs et
 * même checkout que le paywall historique (`create-pass-checkout`) ; retour
 * Stripe sur l'exercice d'origine ou la carte, avec `?pass_success=1`.
 * Web seulement (la page n'affiche aucun achat en natif).
 */
export function SubscriptionAlternative({
  signedIn,
  returnTo,
  onBeforeCheckout,
}: {
  signedIn: boolean;
  returnTo: string;
  onBeforeCheckout?: () => void;
}) {
  const { settings: offerSettings, duration, setDuration } = usePassChoice('30d');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const price = PASS_PRICES.normal[duration];

  async function checkout() {
    setLoading(true);
    setError(null);
    onBeforeCheckout?.();
    markCheckoutStarted(passCheckoutMark('normal', duration));
    const { data, error: fnError } = await supabase.functions.invoke('create-pass-checkout', {
      body: {
        type: 'normal',
        duration,
        return_to: `${window.location.origin}${returnTo}`,
      },
    });
    if (fnError || !data?.url) {
      setError('Le paiement n’a pas pu démarrer. Réessaie dans un instant.');
      setLoading(false);
      return;
    }
    window.location.href = data.url as string;
  }

  return (
    <section className="space-y-2" aria-labelledby="skillcard-sub-title">
      <div className="flex items-center gap-2 pt-2">
        <div className="border-ink-200 flex-1 border-t" />
        <span className="text-ink-500 text-xs font-semibold uppercase tracking-wider">ou</span>
        <div className="border-ink-200 flex-1 border-t" />
      </div>
      <div className="border-accent-300 space-y-4 rounded-2xl border-2 bg-white p-4">
        <div>
          <h2
            id="skillcard-sub-title"
            className="text-ink-900 font-display flex items-center gap-2 text-base font-extrabold"
          >
            <Crown className="text-accent-500 h-4 w-4" aria-hidden />
            Tout JeRaisonne, SKILLCARDS comprises
          </h2>
          <p className="text-ink-600 mt-1 text-sm">
            Toutes les SKILLCARDS de tous les niveaux, tous les exercices et les cours, tant que ton
            accès est actif.
          </p>
        </div>

        <div
          className="grid grid-cols-1 gap-2 sm:auto-cols-fr sm:grid-flow-col"
          role="radiogroup"
          aria-label="Durée"
        >
          {availablePassDurations(offerSettings).map((d) => {
            const selected = duration === d;
            return (
              <button
                key={d}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => setDuration(d)}
                className={cn(
                  'flex min-h-[2.75rem] items-center justify-between gap-2 rounded-xl border-2 px-3 py-2 text-left transition-all active:scale-[0.98] sm:flex-col sm:items-center sm:text-center',
                  selected ? 'border-primary-400 bg-primary-50' : 'border-ink-200 bg-white',
                )}
              >
                <span>
                  <span className="text-ink-900 block text-sm font-bold">{DURATION_LABELS[d]}</span>
                  <span className="text-ink-500 block text-xs">
                    {passSublabel(d, offerSettings)}
                  </span>
                </span>
                <span className="text-ink-900 font-display text-base font-extrabold">
                  {PASS_PRICES.normal[d]}
                </span>
              </button>
            );
          })}
        </div>

        <Button fullWidth disabled={!signedIn || loading} onClick={() => void checkout()}>
          {loading ? 'Redirection…' : passCheckoutLabel(duration, price, offerSettings)}
        </Button>
        {error && (
          <p role="alert" className="text-error-600 text-sm">
            {error}
          </p>
        )}
      </div>
    </section>
  );
}

export function Section({
  icon,
  title,
  children,
}: {
  icon: ReactNode;
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="space-y-2">
      <h2 className="text-ink-700 font-display flex items-center gap-2 text-sm font-bold">
        {icon}
        {title}
      </h2>
      {children}
    </section>
  );
}
